/**
 * AccountManagerTerminalPage — unified dashboard at /admin/account-manager/terminal.
 *
 * Phase 1 (this file): polled, read-only. Renders KPIs above the fold from
 * `/summary` (target <500ms), then layers details (pool, subscriptions,
 * positions) from `/details` (target <2s). 30s polling cadence with
 * tab-visibility pause + BroadcastChannel multi-tab leader election via
 * `usePolledData`.
 *
 * Phases 2-7 layer error feed / inline actions / bulk actions / real-time /
 * audit export / settings panel on top.
 *
 * Auth: AccountManagers / Admins / SuperAdmins only. Non-eligible callers
 * redirect to /. While auth is loading we render `null` to avoid a chrome flash.
 */
import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  AlertCircle,
  Eye,
  Home,
  Pause,
  Pencil,
  Play,
  RefreshCw,
  Terminal as TerminalIcon,
  XCircle,
} from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs';
import EmptyPoolPanel from '../components/account-manager/EmptyPoolPanel';
import MissedExitsSection from '../components/account-manager/MissedExitsSection';
import TerminalFilterBar, {
  TerminalFilters,
} from '../components/account-manager/TerminalFilterBar';
import EditCapitalDialog from '../components/admin/EditCapitalDialog';
import EditGroupDialog from '../components/admin/EditGroupDialog';
import ConfirmActionDialog, { ActionMode } from '../components/account-manager/ConfirmActionDialog';
import accountManagerActionsService from '../services/accountManagerActionsService';
import { useToast } from '../components/common/ToastContainer';
import { useAuth } from '../context/AuthContext';
import { useActingAs } from '../context/ActingAsContext';
import { useNavigate } from 'react-router-dom';
import accountManagerTerminalService from '../services/accountManagerTerminalService';
import { usePolledData } from '../hooks/usePolledData';
import { CHIP_METADATA, CHIP_PILL } from '../utils/chipColors';
import { STATUS_VARIANT_CLASSES, getStatusVariant } from '../utils/statusColors';
import type {
  PoolMember,
  PoolPosition,
  PoolSubscription,
  TerminalDetails,
  TerminalKpis,
} from '../types/accountManager';

const POLL_INTERVAL_MS = 30_000;

function formatINR(v: number): string {
  if (v === 0) return '₹0';
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(abs % 1e7 ? 1 : 0)}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(abs % 1e5 ? 1 : 0)}L`;
  if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(abs % 1000 ? 1 : 0)}K`;
  return `${sign}₹${abs.toFixed(0)}`;
}

const AccountManagerTerminalPage: React.FC = () => {
  const {
    isAccountManager,
    isAdmin,
    isSuperAdmin,
    isLoading: authLoading,
  } = useAuth();
  const { setActingAs } = useActingAs();
  const navigate = useNavigate();
  const toast = useToast();
  const eligible = isAccountManager || isAdmin || isSuperAdmin;

  const [filters, setFilters] = useState<TerminalFilters>({
    search: '',
    onlineOnly: false,
    hasPositions: false,
  });
  const [capitalEditFor, setCapitalEditFor] = useState<PoolMember | null>(null);
  const [groupEditFor, setGroupEditFor] = useState<PoolMember | null>(null);
  // Tab state for the two sections of the terminal page. 'overview' is the
  // existing pool/subscriptions/positions view; 'missed-exits' is the new
  // admin-recovery view that lists positions whose scheduled exit failed.
  const [activeTab, setActiveTab] = useState<'overview' | 'missed-exits'>('overview');
  // Phase 3: single state for the subscription/allocation action dialog —
  // mode determines which form fields render. Subject carries the user-facing
  // display label; ids carry the routing args.
  const [actionState, setActionState] = useState<{
    mode: ActionMode;
    subjectLabel: string;
    userId: string;
    subscriptionId?: string;
    allocationId?: string;
    currentMultiplier?: number;
  } | null>(null);

  // Tiered loading: KPIs first (<500ms target), details after (<2s target).
  // Both poll at 30s. BroadcastChannel name keyed per endpoint so two tabs
  // share one fetch per endpoint.
  const summaryResult = usePolledData<TerminalKpis>({
    fetcher: () => accountManagerTerminalService.getSummary(),
    intervalMs: POLL_INTERVAL_MS,
    channelName: 'am-terminal-summary',
    enabled: eligible && !authLoading,
  });
  const detailsResult = usePolledData<TerminalDetails>({
    fetcher: () => accountManagerTerminalService.getDetails(),
    intervalMs: POLL_INTERVAL_MS,
    channelName: 'am-terminal-details',
    enabled: eligible && !authLoading,
  });

  const refreshAll = () => {
    summaryResult.refresh();
    detailsResult.refresh();
  };

  const handleViewAs = (userId: string) => {
    setActingAs(userId);
    navigate('/schedule');
  };

  // Apply client-side filters to the details payload. Filters are
  // identity-stable so the memo only re-fires when data or filters change.
  const filteredPool = useMemo<PoolMember[]>(() => {
    const pool = detailsResult.data?.pool ?? [];
    const needle = filters.search.trim().toLowerCase();
    return pool.filter((m) => {
      if (filters.onlineOnly && !m.is_online) return false;
      if (filters.hasPositions && m.open_position_count === 0) return false;
      if (needle) {
        const hit =
          (m.full_name || '').toLowerCase().includes(needle)
          || (m.email || '').toLowerCase().includes(needle)
          || (m.primary_client_id || '').toLowerCase().includes(needle)
          || (m.user_id || '').toLowerCase().includes(needle);
        if (!hit) return false;
      }
      return true;
    });
  }, [detailsResult.data, filters]);

  const filteredSubs = useMemo<PoolSubscription[]>(() => {
    const subs = detailsResult.data?.subscriptions ?? [];
    const needle = filters.search.trim().toLowerCase();
    return subs.filter((s) => {
      if (filters.onlineOnly) {
        const member = detailsResult.data?.pool.find((m) => m.user_id === s.user_id);
        if (!member?.is_online) return false;
      }
      if (needle) {
        const hit =
          (s.user_email || '').toLowerCase().includes(needle)
          || (s.basket_id || '').toLowerCase().includes(needle)
          || (s.basket_name || '').toLowerCase().includes(needle)
          || (s.zebu_client_id || '').toLowerCase().includes(needle);
        if (!hit) return false;
      }
      return true;
    });
  }, [detailsResult.data, filters]);

  const filteredPositions = useMemo<PoolPosition[]>(() => {
    const positions = detailsResult.data?.positions ?? [];
    const needle = filters.search.trim().toLowerCase();
    return positions.filter((p) => {
      if (filters.onlineOnly) {
        const member = detailsResult.data?.pool.find((m) => m.user_id === p.user_id);
        if (!member?.is_online) return false;
      }
      if (needle) {
        const hit =
          (p.user_email || '').toLowerCase().includes(needle)
          || (p.symbol || '').toLowerCase().includes(needle)
          || (p.basket_id || '').toLowerCase().includes(needle)
          || (p.client_id || '').toLowerCase().includes(needle);
        if (!hit) return false;
      }
      return true;
    });
  }, [detailsResult.data, filters]);

  // Auth gate. Hooks above always run; redirect renders only after auth resolves.
  if (authLoading) return null;
  if (!eligible) return <Navigate to="/" replace />;

  const summary = summaryResult.data;
  const summaryError = summaryResult.error;
  const detailsError = detailsResult.error;
  const summaryLoadingFirst = summaryResult.isLoading && !summary;

  return (
    <StandardLayout>
      <PageHeader
        title="AccountManager Terminal"
        pageType="management"
        icon={<TerminalIcon className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Account Manager' },
        ]}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={refreshAll}
            leftIcon={
              <RefreshCw
                className={`h-4 w-4 ${summaryResult.isLoading || detailsResult.isLoading ? 'animate-spin' : ''}`}
              />
            }
          >
            Refresh
          </Button>
        }
      />

      {/* Tab navigation between the existing Overview view and the new
       *  Missed Exits recovery view. The Tabs component is uncontrolled-friendly
       *  but we drive it explicitly so the active tab survives re-renders. */}
      <Tabs value={activeTab}>
        <TabsList>
          <TabsTrigger
            value="overview"
            isActive={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="missed-exits"
            isActive={activeTab === 'missed-exits'}
            onClick={() => setActiveTab('missed-exits')}
          >
            Missed Exits
          </TabsTrigger>
        </TabsList>

      <TabsContent value="overview" isActive={activeTab === 'overview'}>
      <div className="space-y-4">
      {/* Top error banner — either /summary or /details fetch failed */}
      {(summaryError || detailsError) && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-800 dark:text-red-200">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>{summaryError || detailsError}</div>
          </div>
        </div>
      )}

      {/* Stat cards — render from /summary, paint above-fold immediately */}
      {summaryLoadingFirst ? (
        <LoadingSpinner message="Loading terminal…" />
      ) : summary ? (
        <>
          {summary.truncated && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-xs text-yellow-800 dark:text-yellow-200">
              Pool is larger than 50 users; showing the first 50. Contact platform support to enable pagination.
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Pool" value={summary.pool_count.toString()} />
            <StatCard
              label="Online"
              value={`${summary.online_count} / ${summary.pool_count}`}
              accent={summary.online_count === summary.pool_count && summary.pool_count > 0 ? 'green' : 'amber'}
            />
            <StatCard label="Open positions" value={summary.total_positions.toString()} />
            <StatCard
              label="Today P&L"
              value={formatINR(summary.today_total_pnl)}
              accent={summary.today_total_pnl >= 0 ? 'green' : 'red'}
            />
          </div>

          {summary.pool_count === 0 ? (
            <EmptyPoolPanel isSuperAdmin={isSuperAdmin} />
          ) : (
            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle>Pool ({filteredPool.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <TerminalFilterBar filters={filters} onChange={setFilters} />
                <PoolTable
                  pool={filteredPool}
                  onViewAs={handleViewAs}
                  onEditCapital={(m) => setCapitalEditFor(m)}
                  onEditGroup={(m) => setGroupEditFor(m)}
                  loading={detailsResult.isLoading && !detailsResult.data}
                />
              </CardContent>
            </Card>
          )}

          {summary.pool_count > 0 && (
            <>
              <Card className="overflow-hidden">
                <CardHeader>
                  <CardTitle>Subscriptions ({filteredSubs.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <SubscriptionsTable
                    subs={filteredSubs}
                    loading={detailsResult.isLoading && !detailsResult.data}
                    onAction={(mode, sub) => {
                      const basketLabel = sub.basket_name || `basket ${sub.basket_id}`;
                      const subjectLabel = sub.user_name
                        ? `${sub.user_name} — ${basketLabel}`
                        : `User ${sub.user_id} — ${basketLabel}`;
                      if (mode === 'unsubscribe' || mode === 'edit-multiplier') {
                        setActionState({
                          mode,
                          subjectLabel,
                          userId: sub.user_id,
                          subscriptionId: sub.subscription_id,
                          currentMultiplier: mode === 'edit-multiplier' ? sub.lot_multiplier : undefined,
                        });
                      } else if ((mode === 'pause' || mode === 'resume') && sub.allocation_id) {
                        setActionState({
                          mode,
                          subjectLabel,
                          userId: sub.user_id,
                          allocationId: sub.allocation_id,
                        });
                      }
                    }}
                  />
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <CardHeader>
                  <CardTitle>Open positions ({filteredPositions.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <PositionsTable
                    positions={filteredPositions}
                    loading={detailsResult.isLoading && !detailsResult.data}
                  />
                </CardContent>
              </Card>
            </>
          )}
        </>
      ) : null}
      </div>
      </TabsContent>

      <TabsContent value="missed-exits" isActive={activeTab === 'missed-exits'}>
        <MissedExitsSection />
      </TabsContent>
      </Tabs>

      {/* Inline capital edit — shares the EditCapitalDialog component from
       *  AdminManagedUsersPage so the on-behalf authz + analytics propagation
       *  paths are identical. */}
      {capitalEditFor && (
        <EditCapitalDialog
          open={true}
          managedUserId={capitalEditFor.user_id}
          managedClientId={capitalEditFor.primary_client_id}
          managedDisplayName={
            capitalEditFor.full_name || capitalEditFor.email || capitalEditFor.user_id
          }
          currentCapital={capitalEditFor.capital ?? 0}
          onClose={() => setCapitalEditFor(null)}
          onSuccess={(newCapital) => {
            toast.showSuccess(`Capital updated to ₹${newCapital.toLocaleString('en-IN')}`);
            setCapitalEditFor(null);
            detailsResult.refresh();
            summaryResult.refresh();
          }}
        />
      )}

      {/* Phase 2 inline client-group edit — same dialog as AdminManagedUsersPage. */}
      {groupEditFor && (
        <EditGroupDialog
          open={true}
          managedUserId={groupEditFor.user_id}
          managedClientId={groupEditFor.primary_client_id}
          managedDisplayName={
            groupEditFor.full_name || groupEditFor.email || groupEditFor.user_id
          }
          currentGroupId={groupEditFor.group_id || null}
          currentGroupName={groupEditFor.group_name}
          onClose={() => setGroupEditFor(null)}
          onSuccess={(_newGroupId, newGroupName) => {
            toast.showSuccess(
              newGroupName ? `Group set to "${newGroupName}"` : 'Group cleared',
            );
            setGroupEditFor(null);
            detailsResult.refresh();
          }}
        />
      )}

      {/* Phase 3 inline action dialog (unsubscribe / edit-multiplier / pause / resume) */}
      {actionState && (
        <ConfirmActionDialog
          open={true}
          mode={actionState.mode}
          subjectLabel={actionState.subjectLabel}
          currentMultiplier={actionState.currentMultiplier}
          onClose={() => setActionState(null)}
          onConfirm={async (payload) => {
            const { userId, subscriptionId, allocationId } = actionState;
            if (actionState.mode === 'unsubscribe' && subscriptionId) {
              await accountManagerActionsService.unsubscribe(
                userId, subscriptionId, payload.reason || '',
              );
              toast.showSuccess('Unsubscribed');
            } else if (actionState.mode === 'edit-multiplier' && subscriptionId && payload.lotMultiplier !== undefined) {
              const r = await accountManagerActionsService.setMultiplier(
                userId, subscriptionId, payload.lotMultiplier,
              );
              toast.showSuccess(
                r.unchanged
                  ? 'Multiplier unchanged'
                  : `Multiplier set to ${r.lot_multiplier}x`,
              );
            } else if (actionState.mode === 'pause' && allocationId) {
              await accountManagerActionsService.pauseAllocation(userId, allocationId);
              toast.showSuccess('Allocation paused');
            } else if (actionState.mode === 'resume' && allocationId) {
              await accountManagerActionsService.resumeAllocation(userId, allocationId);
              toast.showSuccess('Allocation resumed');
            }
            setActionState(null);
            detailsResult.refresh();
          }}
        />
      )}
    </StandardLayout>
  );
};

// ── Stat card ───────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: string;
  accent?: 'green' | 'red' | 'amber';
}> = ({ label, value, accent }) => {
  const accentClass = {
    green: 'text-green-600 dark:text-green-400',
    red: 'text-red-600 dark:text-red-400',
    amber: 'text-amber-600 dark:text-amber-400',
  }[accent ?? 'green'];
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</div>
      <div className={`text-xl font-bold mt-1 tabular-nums ${accent ? accentClass : 'text-gray-900 dark:text-white'}`}>
        {value}
      </div>
    </div>
  );
};

// ── Pool table ──────────────────────────────────────────────────────────────

const PoolTable: React.FC<{
  pool: PoolMember[];
  onViewAs: (userId: string) => void;
  onEditCapital: (member: PoolMember) => void;
  onEditGroup: (member: PoolMember) => void;
  loading: boolean;
}> = ({ pool, onViewAs, onEditCapital, onEditGroup, loading }) => {
  if (loading) return <LoadingSpinner message="Loading pool…" />;
  if (pool.length === 0) {
    return (
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
        No matching users.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
          <tr>
            {['User', 'Status', 'Client ID', 'Group', 'Subscriptions', 'Open positions', 'Capital', ''].map((h) => (
              <th
                key={h}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {pool.map((m) => (
            <tr key={m.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900 dark:text-white">{m.full_name || m.user_id}</div>
                <div className="text-xs text-gray-400 dark:text-gray-500">{m.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {m.is_online ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 dark:text-green-400">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500" /> Online
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span className="inline-block w-2 h-2 rounded-full bg-gray-400" /> Offline
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                {m.primary_client_id || '—'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <div className="inline-flex items-center gap-1.5">
                  {m.group_name ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="inline-block h-2 w-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: m.group_color || '#9ca3af' }}
                      />
                      <span className="text-gray-900 dark:text-white">{m.group_name}</span>
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">—</span>
                  )}
                  <button
                    type="button"
                    onClick={() => onEditGroup(m)}
                    title="Edit client group on behalf of this user"
                    className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    disabled={!m.primary_client_id}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                {m.subscription_count}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                {m.open_position_count}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                <div className="inline-flex items-center gap-1.5">
                  <span>{formatINR(m.capital)}</span>
                  <button
                    type="button"
                    onClick={() => onEditCapital(m)}
                    title="Edit capital on behalf of this user"
                    className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    disabled={!m.primary_client_id}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <button
                  type="button"
                  onClick={() => onViewAs(m.user_id)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 transition-colors"
                  title="Switch view to this user's Schedule page"
                >
                  <Eye className="h-3.5 w-3.5" />
                  View as
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Subscriptions table ─────────────────────────────────────────────────────

const SubscriptionsTable: React.FC<{
  subs: PoolSubscription[];
  loading: boolean;
  onAction: (mode: ActionMode, sub: PoolSubscription) => void;
}> = ({ subs, loading, onAction }) => {
  if (loading) return <LoadingSpinner message="Loading subscriptions…" />;
  if (subs.length === 0) {
    return (
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
        No matching subscriptions.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
          <tr>
            {['User', 'Basket', 'Multiplier', 'Status', 'Client ID', 'Subscribed', 'Actions'].map((h) => (
              <th
                key={h}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {subs.map((s) => {
            // Pause/Resume buttons gate on the ALLOCATION row's status
            // (the subscription row stays ACTIVE while alive). Treat missing
            // / unknown as ACTIVE so a legacy row without allocation_status
            // still shows Pause (the normal first click).
            const isPaused = s.allocation_status === 'INACTIVE';
            return (
              <tr key={s.subscription_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {s.user_name || s.user_id}
                  </div>
                  {s.user_email && (
                    <div className="text-xs text-gray-400 dark:text-gray-500">{s.user_email}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {s.basket_name ? (
                    <>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {s.basket_name}
                      </div>
                      <div
                        className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate max-w-[16rem]"
                        title={s.basket_id}
                      >
                        {s.basket_id}
                      </div>
                    </>
                  ) : (
                    <span
                      className="text-sm font-mono text-gray-500 dark:text-gray-400"
                      title="Listing unpublished or deleted"
                    >
                      {s.basket_id}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                  <div className="inline-flex items-center gap-1.5">
                    <span>{s.lot_multiplier}x</span>
                    <button
                      type="button"
                      onClick={() => onAction('edit-multiplier', s)}
                      title="Edit lot multiplier"
                      className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                  {/* Managed-user status pill — ACTIVE routes through canonical
                      CHIP_PILL.HEALTHY (adds 1px ring; emerald → green within the
                      green family). Inactive/other → CHIP_METADATA.DEFAULT. */}
                  <span
                    className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      s.status === 'ACTIVE' ? CHIP_PILL.HEALTHY : CHIP_METADATA.DEFAULT
                    }`}
                  >
                    {s.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                  {s.zebu_client_id || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                  {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="inline-flex items-center gap-1">
                    {isPaused ? (
                      <button
                        type="button"
                        onClick={() => onAction('resume', s)}
                        title="Resume allocation"
                        disabled={!s.allocation_id}
                        className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Play className="h-3 w-3" />
                        Resume
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onAction('pause', s)}
                        title="Pause allocation"
                        disabled={!s.allocation_id}
                        className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Pause className="h-3 w-3" />
                        Pause
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onAction('unsubscribe', s)}
                      title="Unsubscribe"
                      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 transition-colors"
                    >
                      <XCircle className="h-3 w-3" />
                      Unsubscribe
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Positions table ─────────────────────────────────────────────────────────

const PositionsTable: React.FC<{ positions: PoolPosition[]; loading: boolean }> = ({
  positions,
  loading,
}) => {
  if (loading) return <LoadingSpinner message="Loading positions…" />;
  if (positions.length === 0) {
    return (
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
        No matching open positions.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
          <tr>
            {['User', 'Client ID', 'Symbol', 'Source', 'Status', 'Qty', 'Entry', 'Unrealized P&L', 'Entered'].map((h) => (
              <th
                key={h}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {positions.map((p) => (
            <tr key={p.position_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {p.user_name || p.user_id}
                </div>
                {p.user_email && (
                  <div className="text-xs text-gray-400 dark:text-gray-500">{p.user_email}</div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-700 dark:text-gray-300">
                {p.client_id || '—'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                {p.symbol}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-xs">
                <span
                  className={`font-medium px-2 py-0.5 rounded-full ${
                    p.source === 'marketplace' ? CHIP_METADATA.BTST : CHIP_METADATA.DEFAULT
                  }`}
                  title={
                    p.source === 'marketplace'
                      ? 'Position originated from a marketplace subscription'
                      : "Position created by the managed user's own basket/strategy"
                  }
                >
                  {p.source === 'marketplace' ? '🏪 Marketplace' : '👤 Self'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-xs">
                {/* Position-status chip routed through the canonical
                    `getStatusVariant` + `STATUS_VARIANT_CLASSES` helper used by
                    every other position-status chip in the app. Pre-Phase-0 the
                    inline ternary mapped only OPEN→blue & ENTRY_PENDING→amber
                    with an orange catch-all that swallowed CLOSED + EXIT_PENDING
                    into a wrong hue; the canonical helper resolves each
                    POSITION_STATUS value correctly (OPEN→blue, ENTRY_PENDING /
                    EXIT_PENDING→yellow, CLOSED→gray). */}
                <span
                  className={`font-semibold px-2 py-0.5 rounded-full ${STATUS_VARIANT_CLASSES[getStatusVariant(p.position_status)]}`}
                >
                  {p.position_status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                {p.quantity}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                {p.entry_price ? p.entry_price.toFixed(2) : '—'}
              </td>
              <td
                className={`px-6 py-4 whitespace-nowrap text-sm tabular-nums ${
                  p.unrealized_pnl > 0
                    ? 'text-green-700 dark:text-green-400'
                    : p.unrealized_pnl < 0
                    ? 'text-red-700 dark:text-red-400'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {formatINR(p.unrealized_pnl)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                {p.entered_at
                  ? new Date(p.entered_at).toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: 'Asia/Kolkata',
                    })
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AccountManagerTerminalPage;
